// Browser canvas save shared by browser products.
// The browser canvas path (createImageBitmap -> drawImage -> toBlob) never
// preserves the source ICC color profile or EXIF metadata (native keeps the
// first tile's profile); callers warn so archived colors are not trusted
// blindly. The canvas host is injected so node tests drive the encode path
// with fakes.

import { suggestedNameFor } from "@dezoomify/app-model";
import { failure } from "./failure.ts";

/** Warning logged beside every completed browser save (profile stripped). */
export const BROWSER_SAVE_COLOR_WARNING =
  "Colors may shift slightly: the browser save does not keep the original color profile. For exact colors, use the desktop app.";

export interface CanvasLike {
  toBlob(cb: BlobCallback, mime?: string): void;
}

export interface DocumentLike {
  createElement(tag: string): AnchorLike;
  body: { appendChild(el: unknown): void };
}

export interface AnchorLike {
  href: string;
  download: string;
  click(): void;
  remove(): void;
}

/** Browsers report an origin-tainted canvas as a SecurityError. */
export function isCanvasTaintError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const candidate = error as { name?: unknown; code?: unknown };
  return candidate.name === "SecurityError" || candidate.code === 18;
}

/** Encode the assembled canvas as a PNG Blob (origin-clean only). */
export function canvasToPngBlob(canvas: CanvasLike, signal?: AbortSignal): Promise<Blob> {
  signal?.throwIfAborted();
  return new Promise((resolve, reject) => {
    const abort = () => reject(signal?.reason);
    signal?.addEventListener("abort", abort, { once: true });
    const finish = (blob: Blob | null) => {
      signal?.removeEventListener("abort", abort);
      if (signal?.aborted) {
        reject(signal.reason);
        return;
      }
      if (blob) resolve(blob);
      else
        reject(
          failure(
            "OUTPUT_ENCODE_FAILED",
            "The final picture could not be created from the saved pieces.",
            false,
            undefined,
            "canvas.toBlob returned null while encoding the PNG",
          ),
        );
    };
    try {
      canvas.toBlob(finish, "image/png");
    } catch (e) {
      signal?.removeEventListener("abort", abort);
      if (isCanvasTaintError(e)) {
        reject(e);
        return;
      }
      reject(
        failure(
          "OUTPUT_ENCODE_FAILED",
          "The final picture could not be created from the saved pieces.",
          false,
          undefined,
          `canvas.toBlob threw while encoding the PNG: ${e instanceof Error ? e.message : String(e)}`,
        ),
      );
    }
  });
}

/**
 * Save an already-encoded object URL through a blob anchor (the website save
 * needs no `downloads` permission). The suggested filename carries the
 * canvas dimensions; unknown dimensions fall back to the bare base name.
 */
export function saveBlobViaAnchor(
  doc: DocumentLike,
  blobUrl: string,
  width?: unknown,
  height?: unknown,
  title?: unknown,
): void {
  const anchor = doc.createElement("a");
  anchor.href = blobUrl;
  anchor.download = suggestedNameFor(width, height, "png", title);
  doc.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}
